import React from "react";
import {
  CButton,
  CCol,
  CFormGroup,
  CInputGroup,
  CInputGroupAppend,
  CLabel,
  CRow,
  CTextarea,
} from "@coreui/react";
import Datetime from "react-datetime";
import { useRemissionNoteFormContext } from "../context/RemissionNoteFormContext";
import EstablishmentSelect from "../../establishments/EstablishmentSelect";
import DispatchPointSelect from "../../dispatch-point/DispatchPointSelect";
import StampSelect from "../../stamps/StampSelect";
import ClientSelect from "../../client/ClientSelect";
import { FieldErrors } from "../../form/FieldErrors";
import ClientFieldErrors from "../../form/ClientFieldErrors";
import { getFieldErrors } from "../../../models/errors";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

const SectionDivider: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "16px", marginTop: "16px" }}>
    <div style={{ width: "4px", height: "18px", background: "#006657", borderRadius: "2px", marginRight: "10px", flexShrink: 0 }} />
    <i className={`fa ${icon} mr-2`} style={{ fontSize: "0.85rem", color: "var(--theme-accent)" }}></i>
    <h6 style={{ margin: 0, fontWeight: 600, color: "var(--theme-muted)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.8px" }}>
      {label}
    </h6>
    <div style={{ flex: 1, height: "1px", background: "var(--theme-divider)", marginLeft: "10px" }} />
  </div>
);

const FormLabel: React.FC<{ required?: boolean; children: React.ReactNode }> = ({ required, children }) => (
  <CLabel className="mb-0" style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}>
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

interface RemissionNoteFormHeaderProps {
  onAddClientClick: () => void;
}

export const RemissionNoteFormHeader: React.FC<
  RemissionNoteFormHeaderProps
> = ({ onAddClientClick }) => {
  const {
    remissionNoteRef,
    remissionNoteDate,
    establishment,
    dispatchPoint,
    stamp,
    client,
    errors,
    onEstablishmentChange,
    onDispatchPointChange,
    onStampChange,
    onClientChange,
    onRemissionNoteDateChange,
    onObsChange,
    readonly,
  } = useRemissionNoteFormContext();

  const stamps = useSelector(
    (state: RootState) => state.taxPayerParameters.data.stamps,
  );
  const establishments = useSelector(
    (state: RootState) => state.taxPayerParameters.data.establishments,
  );
  const dispatchPoints = useSelector(
    (state: RootState) => state.taxPayerParameters.data.dispatchPoints,
  );

  const isStampDisabled = stamps.length === 1;
  const isEstablishmentDisabled = establishments.length === 1;
  const isDispatchPointDisabled = dispatchPoints.length === 1;

  const handleEstablishmentChange = (est: typeof establishment) => {
    onEstablishmentChange(est);
    onDispatchPointChange(null);
  };

  const creditNote = remissionNoteRef.current;

  return (
    <>
      <SectionDivider icon="fa-info-circle" label="Datos Generales" />
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Timbrado:</FormLabel>
          </CCol>
          <CCol>
            <fieldset disabled={isStampDisabled}>
              <StampSelect onChange={onStampChange} value={stamp} />
            </fieldset>
            <FieldErrors
              errors={getFieldErrors("stampId", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Establecimiento:</FormLabel>
          </CCol>
          <CCol>
            <fieldset disabled={isEstablishmentDisabled}>
              <EstablishmentSelect
                onChange={handleEstablishmentChange}
                value={establishment}
              />
            </fieldset>
            <FieldErrors
              errors={getFieldErrors("establishmentId", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Punto de Expedición:</FormLabel>
          </CCol>
          <CCol>
            <fieldset disabled={isDispatchPointDisabled}>
              <DispatchPointSelect
                key={establishment?.id ?? 0}
                onChange={onDispatchPointChange}
                value={dispatchPoint}
                establishmentId={establishment?.id}
              />
            </fieldset>
            <FieldErrors
              errors={getFieldErrors("dispatchPointId", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Cliente:</FormLabel>
          </CCol>
          <CCol>
            <CInputGroup>
              <ClientSelect
                editable
                value={client}
                onChange={onClientChange}
              ></ClientSelect>
              <CInputGroupAppend>
                <CButton
                  className="btn btn-primary"
                  style={{ zIndex: 0 }}
                  onClick={onAddClientClick}
                >
                  <i className="fa fa-user-plus"></i>
                </CButton>
              </CInputGroupAppend>
            </CInputGroup>
            <ClientFieldErrors errors={errors} />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Fecha de emisión:</FormLabel>
          </CCol>
          <CCol>
            <Datetime
              className="pl-0"
              onChange={onRemissionNoteDateChange}
              value={remissionNoteDate}
              locale="es/PY"
              dateFormat="DD/MM/YYYY"
              timeFormat="HH:mm"
              closeOnSelect={true}
            ></Datetime>
            <FieldErrors
              errors={getFieldErrors("remissionNoteDate", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel>Observación:</FormLabel>
          </CCol>
          <CCol md={10}>
            <CTextarea
              placeholder="Información adicional"
              defaultValue={creditNote !== null ? creditNote.obs : undefined}
              onChange={onObsChange}
              disabled={readonly}
              style={{ resize: "vertical", minHeight: "70px" }}
            ></CTextarea>
            <FieldErrors
              errors={getFieldErrors("obs", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
    </>
  );
};
